import React, { useMemo } from 'react';
import i18n, { currentLocale } from '../../../../../i18n';
import { dateLocale } from '../../../../../lib/localePrefs';
import { fmt } from '../../../data/metrics';
import type {
  RejectionGridCell,
  ReservationFunnel,
  ReservationFunnelLeakKey,
} from '../../../types';
import { DrillToolbarStat } from '../../shared/DrillToolbar';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-funnel.css';

const t = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(`performance:drillUi.funnel.${key}`, opts);

/** Segments of the requests bar, in funnel order. */
type SegmentKey = 'served' | 'upcoming' | ReservationFunnelLeakKey;
const SEGMENTS: SegmentKey[] = ['served', 'upcoming', 'cancelled', 'noshow', 'rejected', 'pending'];

export type FunnelSegment = {
  key: SegmentKey;
  reservations: number;
  guests: number;
  sharePct: number;
};

/** Served, still ahead and the leaks: together every request received. */
export function funnelSegments(funnel: ReservationFunnel): FunnelSegment[] {
  const leaks = new Map(funnel.leaks.map(l => [l.key, l]));
  return SEGMENTS.map(key => {
    const stage =
      key === 'served' ? funnel.served : key === 'upcoming' ? funnel.upcoming : leaks.get(key);
    return {
      key,
      reservations: stage?.reservations ?? 0,
      guests: stage?.guests ?? 0,
      sharePct: stage?.sharePct ?? 0,
    };
  });
}

/** Signed change in % against the compare period, one decimal; null without a base. */
export function changePct(value: number, prior: number | undefined): number | null {
  if (prior == null || prior <= 0) return null;
  return Math.round(((value - prior) / prior) * 1000) / 10;
}

function pct(v: number | null | undefined): string {
  return v == null ? '–' : `${v}%`;
}

function signed(v: number, unit: string): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v)}${unit}`;
}

function weekdayLabel(dow: number): string {
  // 1 Jan 2024 was a Monday, so day `dow` of that month is ISO weekday `dow`.
  return new Date(Date.UTC(2024, 0, dow)).toLocaleDateString(dateLocale(currentLocale()), {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

function columnLabel(col: { key: string; label: string | null }): string {
  return col.label ?? t('otherShift');
}

/** Heat for a cell: transparent at 0 %, the alert tint at the grid's worst rate. */
function heat(cell: RejectionGridCell, max: number): React.CSSProperties | undefined {
  if (cell.ratePct == null || cell.thin || max <= 0 || cell.ratePct <= 0) return undefined;
  const alpha = Math.min(0.08 + (cell.ratePct / max) * 0.32, 0.4);
  return { background: `color-mix(in srgb, var(--st-alert) ${Math.round(alpha * 100)}%, transparent)` };
}

function GridCell({ cell, max }: { cell: RejectionGridCell; max: number }) {
  if (!cell.requests) {
    return (
      <td className="dfun-cell is-empty" aria-label={t('noRequests')}>
        –
      </td>
    );
  }
  return (
    <td
      className={`dfun-cell${cell.thin ? ' is-thin' : ''}`}
      style={heat(cell, max)}
      title={t('cellTitle', {
        rejected: fmt.num(cell.rejected),
        requests: fmt.num(cell.requests),
      })}
    >
      <span className="dfun-cell-rate">{pct(cell.ratePct)}</span>
      <span className="dfun-cell-n">{i18n.t('performance:drillUi.sampleN', { n: fmt.num(cell.requests) })}</span>
    </td>
  );
}

export function RejectionGrid({ grid }: { grid: ReservationFunnel['grid'] }) {
  if (!grid.columns.length) {
    return <p className="ddrill-tile-note">{t('gridEmpty')}</p>;
  }
  const max = Math.max(
    0,
    ...grid.rows.flatMap(r => r.cells.filter(c => !c.thin).map(c => c.ratePct ?? 0)),
  );
  return (
    <div className="dfun-grid-wrap">
      <table className="dfun-grid">
        <caption className="dfun-grid-cap">
          {grid.kind === 'shift' ? t('gridTitleShift') : t('gridTitleDaypart')}
        </caption>
        <thead>
          <tr>
            <th scope="col" className="dfun-corner">
              <span className="dfun-sr-only">{t('weekday')}</span>
            </th>
            {grid.columns.map(col => (
              <th scope="col" key={col.key}>
                {columnLabel(col)}
              </th>
            ))}
            <th scope="col" className="dfun-total-col">
              {t('allServices')}
            </th>
          </tr>
        </thead>
        <tbody>
          {grid.rows.map(row => (
            <tr key={row.dow}>
              <th scope="row">{weekdayLabel(row.dow)}</th>
              {row.cells.map(cell => (
                <GridCell key={cell.key} cell={cell} max={max} />
              ))}
              <GridCell cell={row} max={max} />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">{t('allDays')}</th>
            {grid.columnTotals.map(cell => (
              <GridCell key={cell.key} cell={cell} max={max} />
            ))}
            <td className="dfun-cell dfun-total-col" aria-hidden />
          </tr>
        </tfoot>
      </table>
      <p className="dfun-grid-legend">
        {t('gridLegend', { n: grid.minRequests })}
      </p>
    </div>
  );
}

function FunnelBar({ segments, total }: { segments: FunnelSegment[]; total: number }) {
  if (total <= 0) return null;
  return (
    <div className="dfun-bar" role="img" aria-label={t('barAria')}>
      {segments
        .filter(s => s.reservations > 0)
        .map(s => (
          <span
            key={s.key}
            className={`dfun-seg dfun-seg-${s.key}`}
            style={{ flexGrow: s.reservations }}
            title={`${t(`stage.${s.key}`)} · ${fmt.num(s.reservations)} · ${pct(s.sharePct)}`}
          />
        ))}
    </div>
  );
}

function StageRow({ segment }: { segment: FunnelSegment }) {
  return (
    <li className={`dfun-leg${segment.reservations ? '' : ' is-zero'}`}>
      <span className={`dfun-dot dfun-seg-${segment.key}`} aria-hidden />
      <span className="dfun-leg-name">{t(`stage.${segment.key}`)}</span>
      <span className="dfun-leg-val">{fmt.num(segment.reservations)}</span>
      <span className="dfun-leg-share">{pct(segment.sharePct)}</span>
      <span className="dfun-leg-guests">
        {t('guests', { count: segment.guests, n: fmt.num(segment.guests) })}
      </span>
    </li>
  );
}

export function DrillFunnelSection({
  funnel,
  compareLabel,
}: {
  funnel: ReservationFunnel;
  /** Compare period label when the drill is comparable. */
  compareLabel?: string;
}) {
  const segments = useMemo(() => funnelSegments(funnel), [funnel]);
  const requested = funnel.requested.reservations;
  const flowing = segments.filter(s => s.key === 'served' || s.key === 'upcoming');
  const leaks = segments.filter(s => s.key !== 'served' && s.key !== 'upcoming');
  const rateDelta = funnel.rejectionRateDeltaPt;
  const priorRate = funnel.prior?.rejectionRate;

  const inspector = useMemo(
    () => (
      <div className="dfun-inspector">
        <table className="dfun-stages">
          <thead>
            <tr>
              <th scope="col">{t('stageCol')}</th>
              <th scope="col">{t('reservationsCol')}</th>
              <th scope="col">{t('guestsCol')}</th>
              <th scope="col">{t('shareCol')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="is-total">
              <th scope="row">{t('stage.requested')}</th>
              <td>{fmt.num(requested)}</td>
              <td>{fmt.num(funnel.requested.guests)}</td>
              <td>{requested ? '100%' : '–'}</td>
            </tr>
            <tr className="is-total">
              <th scope="row">{t('stage.confirmed')}</th>
              <td>{fmt.num(funnel.confirmed.reservations)}</td>
              <td>{fmt.num(funnel.confirmed.guests)}</td>
              <td>{pct(funnel.confirmed.sharePct)}</td>
            </tr>
            {segments.map(s => (
              <tr key={s.key}>
                <th scope="row">{t(`stage.${s.key}`)}</th>
                <td>{fmt.num(s.reservations)}</td>
                <td>{fmt.num(s.guests)}</td>
                <td>{pct(s.sharePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {funnel.prior && compareLabel ? (
          <p className="ddrill-tile-note">
            {t('priorLine', {
              label: compareLabel,
              requested: fmt.num(funnel.prior.requested.reservations),
              confirmed: fmt.num(funnel.prior.confirmed.reservations),
              rate: pct(funnel.prior.rejectionRate),
            })}
          </p>
        ) : null}
        <RejectionGrid grid={funnel.grid} />
      </div>
    ),
    [funnel, segments, requested, compareLabel],
  );

  return (
    <DrillTile
      sectionId="funnel"
      span={12}
      title={t('title')}
      meta={i18n.t('performance:drillUi.nTotal', { n: fmt.num(requested) })}
      inspector={requested > 0 ? inspector : undefined}
      className="dfun-tile"
    >
      {requested <= 0 ? (
        <p className="ddrill-tile-note">{t('empty')}</p>
      ) : (
        <>
          <div className="dfun-steps">
            <div className="dfun-step">
              <span className="dfun-step-lab">{t('stage.requested')}</span>
              <strong>{fmt.num(requested)}</strong>
              <span className="dfun-step-sub">
                {t('guests', { count: funnel.requested.guests, n: fmt.num(funnel.requested.guests) })}
              </span>
            </div>
            <span className="dfun-arrow" aria-hidden>
              →
            </span>
            <div className="dfun-step">
              <span className="dfun-step-lab">{t('stage.confirmed')}</span>
              <strong>{fmt.num(funnel.confirmed.reservations)}</strong>
              <span className="dfun-step-sub">
                {t('ofRequests', { pct: pct(funnel.confirmed.sharePct) })}
              </span>
            </div>
            <span className="dfun-arrow" aria-hidden>
              →
            </span>
            <div className="dfun-step">
              <span className="dfun-step-lab">{t('stage.served')}</span>
              <strong>{fmt.num(funnel.served.reservations)}</strong>
              <span className="dfun-step-sub">
                {funnel.upcoming.reservations > 0
                  ? t('plusAhead', { n: fmt.num(funnel.upcoming.reservations) })
                  : t('ofRequests', { pct: pct(funnel.served.sharePct) })}
              </span>
            </div>
            <div className="dfun-step dfun-rate">
              <span className="dfun-step-lab">{t('rejectionRate')}</span>
              <strong>{pct(funnel.rejectionRate)}</strong>
              <span className="dfun-step-sub">
                {rateDelta != null && compareLabel
                  ? t('rateVs', {
                      delta: signed(rateDelta, 'pt'),
                      prior: pct(priorRate),
                      label: compareLabel,
                    })
                  : i18n.t('performance:drillUi.sampleN', { n: fmt.num(funnel.rateBase) })}
              </span>
            </div>
          </div>
          <FunnelBar segments={segments} total={requested} />
          <ul className="dfun-legend">
            {flowing.map(s => (
              <StageRow key={s.key} segment={s} />
            ))}
            {leaks.map(s => (
              <StageRow key={s.key} segment={s} />
            ))}
          </ul>
          <RejectionGrid grid={funnel.grid} />
          <p className="ddrill-tile-note dfun-foot">
            {t('reasonsNotRecorded')}
            {funnel.statusFiltered ? ` ${t('statusFiltered')}` : ''}
          </p>
        </>
      )}
    </DrillTile>
  );
}

/**
 * Hero stats beside confirmed reservations: requests received and the
 * rejection rate, each with its change against the compare period.
 */
export function DrillFunnelHeroStats({
  funnel,
  trendLabel,
  Trend,
}: {
  funnel: ReservationFunnel;
  trendLabel?: string;
  Trend: React.ComponentType<{
    trendPct: number;
    trendUnit?: string;
    trendLabel?: string;
    goodHigh: boolean;
  }>;
}) {
  const requestsChange = changePct(
    funnel.requested.reservations,
    funnel.prior?.requested.reservations,
  );
  return (
    <>
      <DrillToolbarStat
        label={t('heroRequests')}
        value={fmt.num(funnel.requested.reservations)}
        secondary
        trend={
          requestsChange != null ? (
            <Trend trendPct={requestsChange} trendUnit="%" trendLabel={trendLabel} goodHigh />
          ) : undefined
        }
      />
      <DrillToolbarStat
        label={t('rejectionRate')}
        value={pct(funnel.rejectionRate)}
        secondary
        trend={
          funnel.rejectionRateDeltaPt != null ? (
            <Trend
              trendPct={funnel.rejectionRateDeltaPt}
              trendUnit="pt"
              trendLabel={trendLabel}
              goodHigh={false}
            />
          ) : undefined
        }
      />
    </>
  );
}
